/**
 * One-shot message handed from the moment a session dies to the login screen
 * that renders next.
 *
 * The problem it solves: a deactivated account gets a clean 401, AuthContext
 * clears the token, and ProtectedRoute redirects to /login — where the user
 * sees a perfectly ordinary sign-in form and no explanation at all. Their next
 * move is to type the right password, get "Email or password is incorrect."
 * (because login cannot distinguish deactivation without becoming an
 * enumeration oracle for anonymous callers), and call Joey.
 *
 * sessionStorage, not localStorage, and READ-AND-CLEAR: the notice belongs to
 * this tab and to exactly one render of the login page. A stale "your account
 * was deactivated" surviving into next week would be worse than no message.
 *
 * Storage access is guarded the same way `activeProject.ts` guards it — the web
 * Vitest project runs in the `node` environment where sessionStorage does not
 * exist, and Safari private mode throws on access.
 */

export const AUTH_NOTICE_STORAGE_KEY = 'coffman-tm:auth-notice';

/**
 * The one message the app currently stashes. Kept here rather than in the page
 * so the producer (AuthContext's 401 branch) and the consumer (LoginPage) can
 * never drift apart on the wording.
 */
export const DEACTIVATED_NOTICE =
  'This account has been deactivated. Contact your project administrator.';

function store(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function stashAuthNotice(message: string): void {
  if (!message) return;
  store()?.setItem(AUTH_NOTICE_STORAGE_KEY, message);
}

/** Read the pending notice and consume it. Returns null when there is none. */
export function takeAuthNotice(): string | null {
  const s = store();
  const raw = s?.getItem(AUTH_NOTICE_STORAGE_KEY) ?? null;
  if (raw !== null) s?.removeItem(AUTH_NOTICE_STORAGE_KEY);
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Drop a pending notice without showing it. Signing out deliberately is not a
 * failure, so it must not inherit the previous session's death message.
 */
export function clearAuthNotice(): void {
  store()?.removeItem(AUTH_NOTICE_STORAGE_KEY);
}
